import React, { useState, useEffect, useMemo } from 'react';
import './PokemonDirectory.scss';

const FILTER_CONFIG_POKEMON = {
  types: { 
    label: 'Type', 
    options: ['Bug', 'Dragon', 'Electric', 'Fighting', 'Fire', 'Flying', 'Ghost', 'Grass', 'Ground', 'Ice', 'Normal', 'Poison', 'Psychic', 'Rock', 'Water'] 
  },
  evolutionStages: { 
    label: 'Evolution Stage',
    options: [1, 2, 3] 
  },
  evolutionMethods: { 
    label: 'Evolution Method',
    options: ['None', 'Level', 'Stone', 'Trade'] 
  }
};

const SESSION_STORAGE_FILTERS_KEY_POKEMON = 'pokemonDirectoryFiltersV1';

const getDefaultFiltersPokemon = () => {
  const defaults = {};
  for (const key in FILTER_CONFIG_POKEMON) {
    defaults[key] = [...FILTER_CONFIG_POKEMON[key].options];
  }
  return defaults;
};

const PokemonDirectory = ({ allPokemon, guessedPokemon, show, onClose, onPokemonSelect }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const storedFilters = sessionStorage.getItem(SESSION_STORAGE_FILTERS_KEY_POKEMON);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        // Basic validation to ensure stored filters match current config
        let isValid = true;
        for (const key in FILTER_CONFIG_POKEMON) {
          if (!parsedFilters[key] || !Array.isArray(parsedFilters[key])) {
            isValid = false;
            break;
          }
        }
        if (isValid) return parsedFilters;
      }
    } catch (error) {
      console.error("Error reading Pokemon filters from session storage:", error);
    }
    return getDefaultFiltersPokemon();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_FILTERS_KEY_POKEMON, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving Pokemon filters to session storage:", error);
    }
  }, [filters]);

  const handleFilterChange = (categoryKey, option, isRightClick = false) => {
    setFilters(prevFilters => {
      if (isRightClick) {
        // Right click: Turn off all options in this category except the clicked one
        // and ensure the clicked one is ON
        return {
          ...prevFilters,
          [categoryKey]: [option],
        };
      } else {
        // Left click: Toggle the clicked option on/off
        const currentSelections = prevFilters[categoryKey] || [];
        const newSelections = currentSelections.includes(option)
          ? currentSelections.filter(item => item !== option)
          : [...currentSelections, option];
        
        return { ...prevFilters, [categoryKey]: newSelections };
      }
    });
  };

  const handleResetAllFilters = () => setFilters(getDefaultFiltersPokemon());

  const filteredAndSortedPokemon = useMemo(() => {
    if (!allPokemon || allPokemon.length === 0) return [];

    return allPokemon
      .filter(pokemon => {
        // Type filter (inclusive - check if Pokemon has ANY of the selected types)
        if (filters.types && filters.types.length > 0) {
          const hasSelectedType = filters.types.some(selectedType => 
            pokemon.type1 === selectedType || 
            (pokemon.type2 && pokemon.type2 !== 'None' && pokemon.type2 === selectedType)
          );
          if (!hasSelectedType) return false;
        }

        // Evolution stage filter
        if (filters.evolutionStages && filters.evolutionStages.length > 0) {
          if (!filters.evolutionStages.includes(pokemon.evolution_stage)) {
            return false;
          }
        }

        // Evolution method filter
        if (filters.evolutionMethods && filters.evolutionMethods.length > 0) {
          if (!filters.evolutionMethods.includes(pokemon.evolution_method)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => a.pokedex_number - b.pokedex_number);
  }, [allPokemon, filters]);

  if (!show) {
    return null;
  }

  const handlePokemonClick = (pokemonName) => {
    if (onPokemonSelect) {
      onPokemonSelect(pokemonName);
    }
    onClose();
  };

  const areFiltersDefault = () => {
    const defaultFilters = getDefaultFiltersPokemon();
    return JSON.stringify(filters) === JSON.stringify(defaultFilters);
  };
  const isAnyFilterActive = !areFiltersDefault();

  return (
    <div className="pokemon-directory__overlay" onClick={onClose}>
      <div className="pokemon-directory__panel" onClick={(e) => e.stopPropagation()}>
        <button className="pokemon-directory__close-button" onClick={onClose}>✕</button>
        <div className="pokemon-directory__header">
            <h2>Pokemon Directory</h2>
            {isAnyFilterActive && (
                <button onClick={handleResetAllFilters} className="pokemon-directory__reset-button">
                    Reset Filters
                </button>
            )}
        </div>

        <div className="pokemon-directory__filters">
          {Object.entries(FILTER_CONFIG_POKEMON).map(([categoryKey, config]) => (
            <div key={categoryKey} className="pokemon-directory__filter-category">
              <span className="pokemon-directory__filter-label">{config.label}:</span>
              <div className="pokemon-directory__filter-options">
                {config.options.map(option => (
                  <button
                    key={option}
                    className={`pokemon-directory__filter-button ${
                      filters[categoryKey]?.includes(option) ? 'pokemon-directory__filter-button--selected' : ''
                    }`}
                    onClick={() => handleFilterChange(categoryKey, option, false)}
                    onContextMenu={(e) => {
                      e.preventDefault(); // Prevent default context menu
                      handleFilterChange(categoryKey, option, true);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ul className="pokemon-directory__list">
          {filteredAndSortedPokemon.length > 0 ? (
            filteredAndSortedPokemon.map((pokemon) => (
              <li
                key={pokemon.name} 
                className={`pokemon-directory__list-item ${
                  guessedPokemon?.includes(pokemon.name) ? 'pokemon-directory__list-item--guessed' : ''
                }`}
                onClick={() => {
                  if (!guessedPokemon?.includes(pokemon.name)) {
                    handlePokemonClick(pokemon.name);
                  }
                }}
                style={!guessedPokemon?.includes(pokemon.name) ? { cursor: 'pointer' } : {}}
              >
                <div className="pokemon-directory__item-content">
                  <span className="pokemon-directory__item-name">#{String(pokemon.pokedex_number).padStart(3, '0')} {pokemon.name}</span>
                  <div className="pokemon-directory__type-badges">
                    <span className={`pokemon-directory__type-badge pokemon-directory__type-badge--${pokemon.type1.toLowerCase()}`}>
                      {pokemon.type1.toUpperCase()}
                    </span>
                    {pokemon.type2 && pokemon.type2 !== 'None' && (
                      <span className={`pokemon-directory__type-badge pokemon-directory__type-badge--${pokemon.type2.toLowerCase()}`}>
                        {pokemon.type2.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="pokemon-directory__item-details">
                    <span className="pokemon-directory__item-detail">Stage {pokemon.evolution_stage}</span>
                    <span className="pokemon-directory__item-detail">{pokemon.evolution_method}</span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="pokemon-directory__list-item pokemon-directory__list-item--empty">
              No Pokemon match the current filters.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PokemonDirectory;